import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { detailFooter, detailSocialMedia, getFooter, getSocialMedia } from 'redux-api/slices/footerSlice';
import { BITRIX, PATHS, REQUEST_STATUSES, emailsFooter, externalRecourse } from 'common/constants';
import logo from 'assets/icons/hmLogo.svg';
import { courseLink, withExtraCourses } from 'common/extraCourses';
import classes from './SiteFooter.module.scss';


const SiteFooter = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { footer, footerStatus } = useSelector(detailFooter);
  const { socialMedia } = useSelector(detailSocialMedia);

  const mainLinks = [
    { text: t('Courses'), link: PATHS.allcourses },
    { text: t('Advantages'), link: PATHS.advantages },
    { text: 'Geekstudio', link: externalRecourse.geekstudio, external: true },
    { text: t('Test'), link: BITRIX.test, external: true },
    { text: t('AboutUs'), link: PATHS.aboutUs },
    { text: t('Vacancies'), link: PATHS.vacancies },
    { text: t('FAQ'), link: PATHS.FAQ },
    { text: t('SiteMap'), link: PATHS.mapSite },
    { text: t('PrivacyPolicy'), link: PATHS.privacy },
  ];

  const emails = [
    { title: t('ForTraining'), mail: emailsFooter.forTraining },
    { title: t('ForPartners'), mail: emailsFooter.forPartners },
    { title: t('ForInternship'), mail: emailsFooter.forInternship },
  ];

  useEffect(() => {
    dispatch(getFooter());
    dispatch(getSocialMedia());
    return () => null;
  }, [ dispatch ]);

  if (footerStatus !== REQUEST_STATUSES.SUCCEEDED) return null;

  return (
    <footer className={classes.footer}>
      <div className={classes.container}>
        <div className={classes.top}>
          <Link className={classes.logo} to={PATHS.main}>
            <img src={logo} alt="Geeks" />
          </Link>

          <div className={classes.socials}>
            {socialMedia?.map((item, i) => (
              <a
                className={classes.social}
                href={item.link}
                target="_blank"
                rel="nofollow noreferrer"
                key={item.link ?? i}
              >
                <img src={item.active_icon} alt={item.active_icon_alt || ''} />
              </a>
            ))}
          </div>
        </div>

        <div className={classes.columns}>
          <div className={`${classes.column} ${classes.columnMain}`}>
            <p className={classes.columnTitle}>{t('Main')}</p>
            <ul className={classes.list}>
              {mainLinks.map((item) => (
                <li key={item.text}>
                  {item.external ? (
                    <a className={classes.link} href={item.link} target="_blank" rel="nofollow noreferrer">
                      {item.text}
                    </a>
                  ) : (
                    <Link className={classes.link} to={item.link} rel="nofollow">
                      {item.text}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div className={`${classes.column} ${classes.columnCourses}`}>
            <p className={classes.columnTitle}>{t('Courses')}</p>
            <ul className={classes.list}>
              {withExtraCourses(footer?.courses ?? []).map((item, i) => (
                <li key={item.slug ?? i}>
                  <Link className={classes.link} to={courseLink(item)}>{item.title}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* На мобильном контакты и адреса расходятся по разным колонкам. */}
          <div className={`${classes.column} ${classes.columnWide}`}>
            <div className={`${classes.group} ${classes.groupContacts}`}>
              <p className={classes.columnTitle}>{t('Contacts')}</p>
              <ul className={classes.list}>
                {footer?.numbers?.slice(0, 3).map((number) => (
                  <li key={number}>
                    <a className={classes.link} href={`tel:${number}`}>{number}</a>
                  </li>
                ))}
              </ul>
            </div>

            <div className={`${classes.group} ${classes.groupAddress}`}>
              <p className={classes.columnTitle}>{t('Address')}</p>
              {footer?.locations?.map((item, i) => (
                <div className={classes.cityBlock} key={item.city ?? i}>
                  <p className={classes.city}>{item.city}</p>
                  <ul className={classes.addressList}>
                    {item.address?.map((address, id) => (
                      <li className={classes.address} key={id}>{address}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className={`${classes.column} ${classes.columnMails}`}>
            {emails.map((item) => (
              <div className={classes.mailBlock} key={item.mail}>
                <p className={classes.columnTitle}>{item.title}</p>
                <a className={classes.link} href={`mailto:${item.mail}`}>{item.mail}</a>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={classes.madeBy}>
        <a href={externalRecourse.geekstudio} target="_blank" rel="nofollow noreferrer">
          made by geekstudio
        </a>
      </div>
    </footer>
  );
};

export default SiteFooter;
